/**
 * Buddy's friendly tool sounds — tiny WebAudio-synthesized effects, no files.
 *  - hammer("task created"): two quick cartoon knocks
 *  - drill ("issue registered"): short friendly "drrr"
 *  - swoosh("message sent"): airy whoosh + sparkle ding
 * All best-effort: failures are silently ignored.
 */
let ctx = null;
let VOL = 1; // global tool-sound volume multiplier (0 = muted)
export const setSfxVolume = (v) => { VOL = Math.max(0, Math.min(1, v)); };
import { Capacitor } from "@capacitor/core";
import { playbackFocusAcquire, playbackFocusRelease } from "./wakeword";

const ac = () => {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!ctx) ctx = new AC();
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
};

/** Resume the shared AudioContext during a user gesture — once running it can
 * play Buddy's voice anytime, bypassing desktop autoplay restrictions. */
export function unlockAudio() {
  try { ac(); } catch (e) { /* best-effort */ }
}

/** Play TTS through an HTMLAudioElement. On iOS (WKWebView) this is the ONLY
 * reliable route after the microphone has been used: WebKit leaves the shared
 * WebAudio context "interrupted" once the mic stream stops, so buffer sources
 * go silent. <audio> re-activates the playback audio session at full volume,
 * and Capacitor allows it without a fresh user gesture. */
/** ---- Reminder ALARM ---- Loud two-tone alarm, loops until stopped.
 * Uses a generated WAV in an HTMLAudio loop (reliable on native iOS/Android,
 * same route as Buddy's voice) with a WebAudio fallback for locked autoplay.
 * IGNORES the sfx volume setting on purpose — an alarm must be heard. */
let _alarmUrl = null;

function _buildAlarmWavUrl() {
  const sr = 22050;
  const segs = [];
  for (let i = 0; i < 4; i++) { segs.push([i % 2 ? 760 : 980, 0.13]); segs.push([0, 0.08]); }
  segs.push([0, 0.5]);
  const total = segs.reduce((a, s) => a + s[1], 0);
  const n = Math.floor(sr * total);
  const data = new Int16Array(n);
  let idx = 0;
  for (const [f, d] of segs) {
    const len = Math.floor(sr * d);
    for (let i = 0; i < len && idx < n; i++, idx++) {
      if (f > 0) {
        const tt = i / len;
        const env = Math.min(1, tt * 14, (1 - tt) * 14); // de-click envelope
        data[idx] = Math.round(Math.sin(2 * Math.PI * f * (i / sr)) * 0.92 * env * 32767);
      }
    }
  }
  const bytes = new Uint8Array(44 + data.length * 2);
  const dv = new DataView(bytes.buffer);
  const w = (off, s) => { for (let i = 0; i < s.length; i++) dv.setUint8(off + i, s.charCodeAt(i)); };
  w(0, "RIFF"); dv.setUint32(4, 36 + data.length * 2, true); w(8, "WAVE");
  w(12, "fmt "); dv.setUint32(16, 16, true); dv.setUint16(20, 1, true); dv.setUint16(22, 1, true);
  dv.setUint32(24, sr, true); dv.setUint32(28, sr * 2, true); dv.setUint16(32, 2, true); dv.setUint16(34, 16, true);
  w(36, "data"); dv.setUint32(40, data.length * 2, true);
  new Int16Array(bytes.buffer, 44).set(data);
  return URL.createObjectURL(new Blob([bytes], { type: "audio/wav" }));
}

function _alarmBeepWebAudio() {
  try {
    const c = ac();
    if (!c || c.state !== "running") return;
    const now = c.currentTime;
    [980, 760, 980].forEach((f, i) => {
      const o = c.createOscillator();
      const g = c.createGain();
      o.type = "square"; o.frequency.value = f;
      o.connect(g); g.connect(c.destination);
      const t0 = now + i * 0.21;
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.5, t0 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.16);
      o.start(t0); o.stop(t0 + 0.18);
    });
  } catch (e) { /* best-effort */ }
}

export async function startAlarmSound() {
  try {
    if (!_alarmUrl) _alarmUrl = _buildAlarmWavUrl();
    const audio = new Audio(_alarmUrl);
    audio.loop = true;
    audio.volume = 1;
    await audio.play(); // throws if autoplay is blocked
    return () => {
      // gentle stop: pause only — clearing src forces an extra audio-session
      // reconfiguration on iOS, which we want to avoid while closing the alarm
      try { audio.loop = false; audio.pause(); } catch (e) { /* ignore */ }
    };
  } catch (e) {
    _alarmBeepWebAudio();
    const iv = setInterval(_alarmBeepWebAudio, 1300);
    return () => clearInterval(iv);
  }
}

export const BUDDY_SPEEDS = [1, 1.5, 2];
/** Buddy's TTS playback speed (1 / 1.5 / 2), persisted; pitch is preserved by HTMLAudio. */
export const getBuddySpeed = () => {
  try {
    const v = parseFloat(localStorage.getItem("osc_buddy_speed") || "1");
    return BUDDY_SPEEDS.includes(v) ? v : 1;
  } catch (e) { return 1; }
};
export const setBuddySpeed = (v) => { try { localStorage.setItem("osc_buddy_speed", String(v)); } catch (e) { /* ignore */ } };

const applyRate = (el) => {
  const rate = getBuddySpeed();
  try { el.preservesPitch = true; el.webkitPreservesPitch = true; el.playbackRate = rate; el.defaultPlaybackRate = rate; } catch (e) { /* ignore */ }
  return rate;
};

async function playViaElement(base64, { onStart, onEnd } = {}) {
  try {
    const audio = new Audio(`data:audio/mp3;base64,${base64}`);
    audio.preload = "auto";
    const rate = applyRate(audio);
    let done = false;
    let watchdog = null;
    const finish = () => {
      if (done) return;
      done = true;
      if (watchdog) clearTimeout(watchdog);
      try { onEnd?.(); } catch (e) { /* ignore */ }
    };
    audio.onended = finish;
    audio.onerror = finish;
    // iOS WKWebView occasionally swallows "ended" for data-URL audio.
    // Belt & braces: watch the playhead AND arm a duration-based watchdog so
    // Buddy is NEVER stuck in "talking" waiting for an event that won't come.
    audio.ontimeupdate = () => {
      if (isFinite(audio.duration) && audio.duration > 0 && audio.currentTime >= audio.duration - 0.12) finish();
    };
    const armWatchdog = () => {
      if (done || watchdog) return;
      const secs = isFinite(audio.duration) && audio.duration > 0 ? audio.duration : 60;
      watchdog = setTimeout(finish, (secs / rate) * 1000 + 2000);
    };
    audio.onloadedmetadata = armWatchdog;
    await audio.play(); // throws if playback is blocked
    applyRate(audio); // some engines reset the rate on play()
    try { onStart?.(); } catch (e) { /* ignore */ }
    armWatchdog();
    return {
      pause: () => {
        done = true; // manual stop — suppress onEnd (matches WebAudio path)
        if (watchdog) clearTimeout(watchdog);
        try { audio.pause(); audio.src = ""; } catch (e) { /* ignore */ }
      },
    };
  } catch (e) {
    return null;
  }
}

/** Play Buddy's TTS reply. Native app: HTMLAudio first (see playViaElement).
 * Desktop web: Web Audio (reliable where HTMLAudio autoplay is blocked
 * without a fresh user gesture), falling back to HTMLAudio.
 * Returns a handle { pause() } or null when playback isn't possible. */
export async function playVoice(base64, { onStart, onEnd } = {}) {
  return withPlaybackFocus((opts) => _playVoiceInner(base64, opts), { onStart, onEnd });
}

/** Holds the iOS playback focus (pauses the "Hey Buddy" recognizer) for the
 * whole life of a playback handle: acquired before play, released on end,
 * error or manual pause. Never lets a failure leak the lock. */
async function withPlaybackFocus(play, { onStart, onEnd } = {}) {
  let released = false;
  const release = () => { if (!released) { released = true; playbackFocusRelease(); } };
  await playbackFocusAcquire();
  let handle = null;
  try {
    handle = await play({ onStart, onEnd: () => { release(); try { onEnd?.(); } catch (e) { /* ignore */ } } });
  } catch (e) {
    handle = null;
  }
  if (!handle) { release(); return null; }
  return { ...handle, pause: () => { try { handle.pause(); } finally { release(); } } };
}

async function _playVoiceInner(base64, { onStart, onEnd } = {}) {
  if (Capacitor.isNativePlatform() || getBuddySpeed() !== 1) {
    // HTMLAudio keeps the pitch natural when sped up; Web Audio would chipmunk the voice
    const handle = await playViaElement(base64, { onStart, onEnd });
    if (handle) return handle;
    // fall through to WebAudio as a last resort
  }
  try {
    const c = ac();
    if (!c) return playViaElement(base64, { onStart, onEnd });
    if (c.state === "suspended") { try { await c.resume(); } catch (e) { /* ignore */ } }
    if (c.state !== "running") return playViaElement(base64, { onStart, onEnd });
    const bin = atob(base64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const buf = await c.decodeAudioData(bytes.buffer);
    const src = c.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = getBuddySpeed();
    src.connect(c.destination);
    let stopped = false;
    src.onended = () => { if (!stopped) { try { onEnd?.(); } catch (e) { /* ignore */ } } };
    try { onStart?.(); } catch (e) { /* ignore */ }
    src.start();
    return { pause: () => { stopped = true; try { src.stop(); } catch (e) { /* ignore */ } } };
  } catch (e) {
    return playViaElement(base64, { onStart, onEnd });
  }
}

// --- streamed voice playback -------------------------------------------------
let voiceEl = null; // shared <audio>, unlocked during a user gesture
const SILENT_WAV = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=";

/** Call from a USER GESTURE (e.g. the button that opens the interpreter):
 * plays a silent clip on a reusable <audio> element so that swapping .src and
 * calling play() later (streamed TTS) is allowed on iOS/Safari/Chrome. */
export function primeVoicePlayback() {
  try {
    if (!voiceEl) {
      voiceEl = new Audio();
      voiceEl.preload = "auto";
      voiceEl.playsInline = true;
    }
    voiceEl.src = SILENT_WAV;
    const p = voiceEl.play();
    if (p && p.catch) p.catch(() => {});
  } catch (e) { /* ignore */ }
}

const bufToB64 = (buf) => {
  const bytes = new Uint8Array(buf);
  let bin = "";
  const CH = 0x8000;
  for (let i = 0; i < bytes.length; i += CH) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CH));
  }
  return btoa(bin);
};

/**
 * Streamed voice playback from a URL (audio/mpeg StreamingResponse).
 * 1) Tries the primed HTMLAudio element — playback starts on the first bytes.
 * 2) If autoplay is blocked (no primed element / stale gesture), DOWNLOADS the
 *    audio and plays it through playVoice() — the same reliable path Buddy
 *    always used (WebAudio on web, HTMLAudio on native).
 */
export async function playVoiceUrl(url, { onStart, onEnd } = {}) {
  return withPlaybackFocus((opts) => _playVoiceUrlInner(url, opts), { onStart, onEnd });
}

async function _playVoiceUrlInner(url, { onStart, onEnd } = {}) {
  try {
    const el = voiceEl || new Audio();
    try { el.pause(); } catch (e) { /* ignore */ }
    el.preload = "auto";
    el.src = url;
    let done = false;
    const finish = () => { if (!done) { done = true; try { onEnd?.(); } catch (e) { /* ignore */ } } };
    el.onended = finish;
    el.onerror = finish;
    applyRate(el);
    await el.play();
    applyRate(el);
    try { onStart?.(); } catch (e) { /* ignore */ }
    return {
      pause: () => { try { el.pause(); el.removeAttribute("src"); } catch (e) { /* ignore */ } finish(); },
    };
  } catch (e) { /* autoplay blocked — fall through to the download path */ }
  try {
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`tts http ${resp.status}`);
    const b64 = bufToB64(await resp.arrayBuffer());
    return await _playVoiceInner(b64, { onStart, onEnd });
  } catch (e) {
    try { onEnd?.(); } catch (e2) { /* ignore */ }
    return null;
  }
}

const knock = (c, at, freq = 160) => {
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = "triangle";
  osc.frequency.setValueAtTime(freq, at);
  osc.frequency.exponentialRampToValueAtTime(freq * 0.55, at + 0.08);
  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.exponentialRampToValueAtTime(0.28 * VOL, at + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.12);
  osc.connect(gain);
  gain.connect(c.destination);
  osc.start(at);
  osc.stop(at + 0.14);
  // wooden "click" transient
  const buf = c.createBuffer(1, c.sampleRate * 0.03, c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length);
  const src = c.createBufferSource();
  src.buffer = buf;
  const ng = c.createGain();
  ng.gain.setValueAtTime(0.12 * VOL, at);
  ng.gain.exponentialRampToValueAtTime(0.0001, at + 0.03);
  const hp = c.createBiquadFilter();
  hp.type = "highpass";
  hp.frequency.value = 1200;
  src.connect(hp);
  hp.connect(ng);
  ng.connect(c.destination);
  src.start(at);
};

export function soundHammer() {
  try {
    if (VOL <= 0) return;
    const c = ac();
    if (!c) return;
    const now = c.currentTime;
    knock(c, now + 0.01, 170);
    knock(c, now + 0.19, 150);
  } catch (e) { /* best-effort */ }
}

export function soundDrill() {
  try {
    if (VOL <= 0) return;
    const c = ac();
    if (!c) return;
    const now = c.currentTime;
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(190, now);
    osc.frequency.linearRampToValueAtTime(320, now + 0.28);
    // vibrato = the "drrr"
    const lfo = c.createOscillator();
    const lfoGain = c.createGain();
    lfo.type = "square";
    lfo.frequency.value = 34;
    lfoGain.gain.value = 42;
    lfo.connect(lfoGain);
    lfoGain.connect(osc.frequency);
    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 1400;
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.12 * VOL, now + 0.03);
    gain.gain.setValueAtTime(0.12 * VOL, now + 0.24);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.34);
    osc.connect(lp);
    lp.connect(gain);
    gain.connect(c.destination);
    lfo.start(now);
    osc.start(now);
    osc.stop(now + 0.36);
    lfo.stop(now + 0.36);
  } catch (e) { /* best-effort */ }
}

export function soundSwoosh() {
  try {
    if (VOL <= 0) return;
    const c = ac();
    if (!c) return;
    const now = c.currentTime;
    // airy whoosh (filtered noise sweep)
    const buf = c.createBuffer(1, c.sampleRate * 0.3, c.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    const src = c.createBufferSource();
    src.buffer = buf;
    const bp = c.createBiquadFilter();
    bp.type = "bandpass";
    bp.Q.value = 1.1;
    bp.frequency.setValueAtTime(500, now);
    bp.frequency.exponentialRampToValueAtTime(2600, now + 0.22);
    const ng = c.createGain();
    ng.gain.setValueAtTime(0.0001, now);
    ng.gain.exponentialRampToValueAtTime(0.14 * VOL, now + 0.05);
    ng.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);
    src.connect(bp);
    bp.connect(ng);
    ng.connect(c.destination);
    src.start(now);
    // sparkle ding at the end
    const osc = c.createOscillator();
    const og = c.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(1180, now + 0.18);
    og.gain.setValueAtTime(0.0001, now + 0.18);
    og.gain.exponentialRampToValueAtTime(0.1 * VOL, now + 0.2);
    og.gain.exponentialRampToValueAtTime(0.0001, now + 0.42);
    osc.connect(og);
    og.connect(c.destination);
    osc.start(now + 0.18);
    osc.stop(now + 0.44);
  } catch (e) { /* best-effort */ }
}
